import { useLoader } from "../../../../../context/LoaderContext"
import {
  Modal,
  Table,
  InputNumber,
  Tooltip,
  Button,
  Switch,
  Form,
  message,
  notification,
  Tag,
  Spin,
  Card,
  Space,
  Alert,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addNotesApprenants,
  getDocByIdAffaire,
  getNotesApprenants,
  getUploadPvSession,
  updateActionAffaire,
  updateNotesApprenants,
  uploadDocumentAffaire,
  uploadPVSession,
} from "../../../../../api/geoprod";
import { useLocation, useNavigate } from "react-router-dom";
import classes from "../../../../Etablissements/Etablissements.module.css";
import { InboxOutlined } from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import { BookOpen, CircleArrowLeft } from "lucide-react";

const SaisieNotes = () => {
  const { t } = useTranslation();
  const { setIsLoading, isLoading } = useLoader();
  const navigate = useNavigate();
  const [modalVisible, setModalVisible] = useState(false);
  const [notes, setNotes] = useState({});
  const [existingNotes, setExistingNotes] = useState({ examen: [], rattrapage: [], decisions: [] });
  const [form] = Form.useForm();
  const [rattrapageEnabled, setRattrapageEnabled] = useState({});
  const [fileList, setFileList] = useState([]);
  const [decisions, setDecisions] = useState({});
  
  const params = useLocation();
  const id_status = params?.state?.id_status || sessionStorage.getItem("id_statut_session_formation_homologuee");
  const listeApprenants = params?.state?.listeApprenants;
  const periode = params?.state?.periode;
  const year = params?.state?.year;
  const listeModules = params?.state?.listeModules;
  const isLastConsulterNote = params?.state?.isLastConsulterNote; 
  const id_affaire = params?.state?.id_affaire;
  const rattrapage = params?.state?.rattrapage;
  const isLastRecord = params?.state?.isLastRecord;

  useEffect(() => {
    const fetchData = async () => {
      if (id_status === import.meta.env.VITE_APP_ID_STATUS_DECISION_FINALE_REFUS_NOTES_APPRENANT_SESSION_HOMOLOGUEE && isLastConsulterNote) {
        setIsLoading(true);
        try {
          const response = await getNotesApprenants(id_affaire, { "periode": periode });
          
          // Extraction sécurisée des données
          const firstListKey = Object.keys(response?.list || {})[0];
          const notesData = firstListKey && response?.list[firstListKey]?.[periode] 
            ? { 
                examen: response.list[firstListKey][periode]?.examen || [],
                rattrapage: response.list[firstListKey][periode]?.rattrapage || [],
                decisions: response.list[firstListKey][periode]?.decisions || []
              }
            : { examen: [], rattrapage: [], decisions: [] };

          setExistingNotes(notesData);

          // Initialisation des notes et switches
          const initialNotes = {};
          const initialRattrapageEnabled = {};
          
          notesData.examen.forEach(note => {
            const key = `${note?.id_apprenant}-${note?.key_module}-examen`;
            initialNotes[key] = parseFloat(note?.note);
            form.setFieldsValue({ [key]: parseFloat(note?.note) });
          });
          
          if (notesData.rattrapage?.length > 0) {
            notesData.rattrapage.forEach(note => {
              const key = `${note?.id_apprenant}-${note?.key_module}-rattrapage`;
              const switchKey = `${note?.id_apprenant}-${note?.key_module}`;
              
              initialNotes[key] = parseFloat(note?.note);
              initialRattrapageEnabled[switchKey] = true;
              
              form.setFieldsValue({ 
                [key]: parseFloat(note?.note),
                [switchKey]: true 
              });
            });
          }
          
          setNotes(initialNotes);
          setRattrapageEnabled(initialRattrapageEnabled);

          // Gestion des décisions
          const decisionsMap = {};
          notesData.decisions?.forEach(decision => {
            decisionsMap[decision.id_apprenant] = {
              decision: decision.directeur_decision,
              commentaire: decision.directeur_commentaire
            };
          });
          setDecisions(decisionsMap);

          // Gestion du fichier PV
          const pvResponse = await getUploadPvSession(id_affaire,periode);
          let found = false;
          Object?.entries(pvResponse?.pvs || {}).forEach(([annee, semestres]) => {
            if (!found && semestres[periode] && semestres[periode]?.length > 0) {
              found = true; 
              setFileList([{
                uid: '-1',
                name: semestres[periode][0]?.url.split('/').pop(),
                status: 'done',
                url: semestres[periode][0]?.url
              }]);
              form.setFieldsValue({ pv_session: semestres[periode][0]?.url });
            }
          });

          setIsLoading(false);
        } catch (error) {
          notification.error({
            message: t("Erreur"),
            description: error?.data?.message || t("Une erreur est survenue"),
            duration: 5,
          });
          setIsLoading(false);
        }
      }
    };
    fetchData();
  }, []);

  const handleNoteChange = (apprenantId, moduleKey, type, value) => {
    const key = `${apprenantId}-${moduleKey}-${type}`;
    setNotes((prev) => ({ ...prev, [key]: value }));
  };

  const handleRattrapageSwitch = (apprenantId, moduleKey, checked) => {
    const key = `${apprenantId}-${moduleKey}`;
    setRattrapageEnabled((prev) => ({ ...prev, [key]: checked }));

    if (!checked) {
      const noteKey = `${apprenantId}-${moduleKey}-rattrapage`;
      setNotes((prev) => {
        const newNotes = { ...prev };
        delete newNotes[noteKey];
        return newNotes;
      });
      form.setFieldValue(noteKey, undefined);
    }
  };

  const hasRattrapageNotes = existingNotes.rattrapage?.length > 0;
  const hasAnyDecision = existingNotes.decisions?.some(d => d.directeur_decision !== undefined);
  const hasAnyComment = existingNotes.decisions?.some(d => d.directeur_commentaire);

  const moduleSubColumns = listeModules?.examen?.map((mod) => {
    const moduleKey = mod.module_key;
    const uniteSeuil = mod?.unite_seuil;
    
    const min = 0;
    const max = uniteSeuil && uniteSeuil === "pourcentage" ? 100 : 20;

    const children = [
      {
        title: "Examen",
        dataIndex: `examen-${moduleKey}`,
        key: `examen-${moduleKey}`,
        render: (_, record) => {
          const fieldName = `${record.id}-${moduleKey}-examen`;
          const existingNote = existingNotes?.examen?.find(
            n => n?.id_apprenant === record?.id && n?.key_module === moduleKey
          );
          
          return (
            <Form.Item
              name={fieldName}
              initialValue={existingNote ? parseFloat(existingNote?.note) : undefined}
              rules={[
                    { required: true, message: t("Veuillez remplir ce champ.") },
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null || value === "") {
                          return Promise.resolve(); 
                        }
                        if (value < min) {
                          return Promise.reject(new Error(t(`La note doit être supérieure ou égale à ${min}.`)));
                        }
                        if (value > max) {
                          return Promise.reject(new Error(t(`La note doit être inférieure ou égale à ${max}.`)));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
             
            >
              <InputNumber
                size="small"
                // min={min}
                // max={max}
                disabled={modalVisible || decisions[record.id]?.decision === 1}
                onChange={(value) =>
                  handleNoteChange(record.id, moduleKey, "examen", value)
                }
              />
            </Form.Item>
          );
        },
      },
    ];
// debugger
    if (rattrapage) {
      
      children.push({
        title: "Rattrapage",
        dataIndex: `rattrapage-${moduleKey}`,
        key: `rattrapage-${moduleKey}`,
        render: (_, record) => {
          const switchKey = `${record.id}-${moduleKey}`;
          const isEnabled = rattrapageEnabled[switchKey] ?? false;
          const fieldName = `${record.id}-${moduleKey}-rattrapage`;
          const existingRattrapage = existingNotes?.rattrapage?.find(
            n => n.id_apprenant === record.id && n.key_module === moduleKey
          );

          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
              <Switch
                checked={isEnabled}
                disabled={modalVisible || decisions[record.id]?.decision === 1}
                onChange={(checked) =>
                  handleRattrapageSwitch(record.id, moduleKey, checked)
                }
                size="small"
              />
              <Form.Item
                name={fieldName}
                initialValue={existingRattrapage ? parseFloat(existingRattrapage.note) : undefined}
                // rules={[{ required: isEnabled, message: t("Veuillez remplir ce champ.") }]}
                 rules={[
                    {
                      required: isEnabled,
                      message: t("Veuillez remplir ce champ."),
                    },
                    {
                      validator: (_, value) => {
                        if (!isEnabled) return Promise.resolve();
                        if (value === undefined || value === null || value === "") {
                          return Promise.resolve();
                        }
                        if (value < min) {
                          return Promise.reject(new Error(t(`La valeur doit être supérieure ou égale à ${min}.`)));
                        }
                        if (value > max) {
                          return Promise.reject(new Error(t(`La valeur doit être inférieure ou égale à ${max}.`)));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  // min={min}
                  // max={max}
                  size="small"
                  disabled={!isEnabled || modalVisible || decisions[record.id]?.decision === 1}
                  onChange={(value) =>
                    handleNoteChange(record.id, moduleKey, "rattrapage", value)
                  }
                />
              </Form.Item>
            </div>
          );
        },
      });
    }

    children.push({
      title: "Résultat",
      dataIndex: `resultat-${moduleKey}`,
      key: `resultat-${moduleKey}`,
      render: (_, record) => {
        const examenKey = `${record.id}-${moduleKey}-examen`;
        const rattrapageKey = `${record.id}-${moduleKey}-rattrapage`;
        const switchKey = `${record.id}-${moduleKey}`;
        const isRattrapageEnabled = rattrapageEnabled[switchKey] ?? false;
        const noteExamen = notes[examenKey];
        const noteRattrapage = notes[rattrapageKey];
        const seuil = mod?.seuil ;

        if (noteExamen == null) {
          return <Tag color="yellow">{import.meta.env.VITE_APP_ID_STATUS_MODULE_APPRENANT_A_SAISIR}</Tag>;
        }

        if (noteExamen >= seuil) {
          return <Tag color="green"> {import.meta.env.VITE_APP_ID_STATUS_MODULE_APPRENANT_VALIDEE}</Tag>;
        }

        if (noteExamen < seuil && isRattrapageEnabled) {
          if (noteRattrapage >= seuil) {
            return <Tag color="green">{import.meta.env.VITE_APP_ID_STATUS_MODULE_APPRENANT_VALIDEE}</Tag>;
          } else {
            return <Tag color="red"> {import.meta.env.VITE_APP_ID_STATUS_MODULE_APPRENANT_NON_VALIDEE}</Tag>;
          }
        }

        return <Tag color="red">{import.meta.env.VITE_APP_ID_STATUS_MODULE_APPRENANT_NON_VALIDEE}</Tag>;
      },
    });

    return {
      title: (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Tooltip title={`${mod.name_module}`}>
            <strong>{`${mod.module_key} (Seuil: ${mod?.seuil + " " + mod?.unite_seuil})`}</strong>
          </Tooltip>
        </div>
      ),
      children,
    };
  });

  const getFullName = (record) => {
    const isArabic =
      localStorage.getItem("language") === "ar" && record?.name_ar;
    const parts = isArabic
      ? [record.surname_ar, record.name_ar]
      : [record.surname, record.name];
    return parts.filter(Boolean).join(" ");
  };

  const columns = [
    {
      title: t("Apprenant"),
      dataIndex: "apprenant",
      key: "apprenant",
      render: (_, record) => getFullName(record),
      fixed: 'left'
    },
    {
      title: "Liste des modules",
      children: moduleSubColumns,
    },
    ...(hasAnyDecision ? [{
      title: "Décision directeur",
      dataIndex: "resultat_final",
      key: "resultat_final",
      fixed: 'right',
      render: (_, record) => {
        
        const decision = decisions[record.id]?.decision;
        return decision === 1 ? 
          <Tag color="green">Accepté</Tag> : 
          decision === 0 ? <Tag color="red">Refusé</Tag> :<Tag color="yellow">Pas encore</Tag>;
      },
    }] : []),
    ...(hasAnyComment ? [{
      title: "Commentaire directeur",
      dataIndex: "commentaire",
      key: "commentaire",
      fixed: 'right',
      render: (_, record) => (
        <p>{decisions[record.id]?.commentaire}</p>
      ),
    }] : [])
  ].filter(Boolean);

  const dataSource = listeApprenants.map((a) => ({
    key: a.id,
    id: a.id,
    name: a.name,
    name_ar: a.name_ar,
    surname: a.surname,
    surname_ar: a.surname_ar,
  }));

  const submitFileAndMentions = (formData, newFileName) => {
    uploadPVSession(formData)
      .then(async (responseUpload) => {
        setFileList([
          {
            uid: "-1",
            name: newFileName,
            status: "done",
            url: responseUpload?.data?.path,
          },
        ]);

        const examModulesMap = Object?.fromEntries(
          (listeModules?.examen || []).map((m) => [
            m?.module_key,
            m?.id_examen_module,
          ])
        );
        const rattrapageModulesMap = Object?.fromEntries(
          (listeModules?.rattrapage || []).map((m) => [
            m?.module_key,
            m?.id_examen_module,
          ])
        );

        const payloadNotes = [];

        Object?.entries(notes)?.forEach(([key, note]) => {
          const [id_apprenant, module_key, type] = key?.split("-");
          const id_examen =
            type === "examen"
              ? examModulesMap[module_key]
              : rattrapageModulesMap[module_key];

          if (id_examen) {
            payloadNotes.push({
              id_apprenant: Number(id_apprenant),
              id_examen: parseInt(id_examen),
              note,
            });
          }
        });

        const payloadAddNotes = {
          apprenants: payloadNotes,
          id_session: id_affaire,
          semestre_finale: isLastRecord ? 1 : 0
        };
        

        if (id_status === import.meta.env.VITE_APP_ID_STATUS_DECISION_FINALE_REFUS_NOTES_APPRENANT_SESSION_HOMOLOGUEE && isLastConsulterNote) {
          updateNotesApprenants(payloadAddNotes)
            .then(async (response) => {
              notification.success({
                message: t("Succès"),
                description: t(response?.data?.message),
                duration: 0,
              });
              navigate("/apprenants/notes", {
   
              state: {
                id_affaire: id_affaire,
                periode : periode
               
              },
              });
            })
            .catch((error) => {
              setIsLoading(false);
              notification?.error({
                message: t("Erreur"),
                description: t(error?.data?.message),
                duration: 0,
              });
            });
        } else {
          addNotesApprenants(payloadAddNotes)
            .then(async (response) => {
              notification.success({
                message: t("Succès"),
                description: t(response?.data?.message),
                duration: 0,
              });
              setModalVisible(false);
              navigate("/apprenants/notes", {
               
                state: {
                id_affaire: id_affaire,
                periode : periode
               
              },
              });
            })
            .catch((error) => {
              setIsLoading(false);
              notification?.error({
                message: t("Erreur"),
                description: t(error?.data?.message),
                duration: 0,
              });
            });
        }
      })
      .catch((error) => {
        notification.error({
          message: t("Erreur"),
          description: t(
            "Une erreur est survenue lors du téléversement de votre document. Veuillez réessayer."
          ),
          duration: 0,
        });
        setIsLoading(false);
      });
  };

  const soumettreNote = () => {
    setModalVisible(false);
    setIsLoading(true);
    let newFileName = "";
    const formData = new FormData();

    if (fileList[0]?.originFileObj) {
      newFileName = `PV_${periode} ${fileList[0]?.originFileObj?.name}`;
      const renamedFile = new File(
        [fileList[0]?.originFileObj],
        newFileName,
        { type: fileList[0]?.originFileObj?.type }
      );

      formData.append("id_session", id_affaire);
      formData.append(
        "id_type_doc",
        import.meta.env.VITE_APP_ID_TYPE_DOC_PV_SESSION_FORMATION
      );
      formData.append("pv", renamedFile);
      formData.append("annee", year);
      formData.append("periode", periode);

      submitFileAndMentions(formData, newFileName);
    } else {
      const fileUrl = fileList[0]?.url;
      newFileName = fileList[0]?.name;

      fetch(fileUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const fileFromUrl = new File([blob], newFileName, { type: blob.type });

          formData.append("id_session", id_affaire);
          formData.append(
            "id_type_doc",
            import.meta.env.VITE_APP_ID_TYPE_DOC_PV_SESSION_FORMATION
          );
          formData.append("pv", fileFromUrl);
          formData.append("annee", year);
          formData.append("periode", periode);

          submitFileAndMentions(formData, newFileName);
        })
        .catch(() => {
          notification.error({
            message: t("Erreur"),
            description: t("Impossible de récupérer le fichier."),
            duration: 0,
          });
          setIsLoading(false);
        });
    }
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setModalVisible(true);
      })
      .catch(() => {
        setModalVisible(false);
        setIsLoading(false);
        notification.error({
          message: "Erreur",
          description: "Veuillez vérifier les champs obligatoires avant de soumettre.",
        });
      });
  };

  const handleChangeUpload = ({ file, fileList }) => {
    if (file.status !== 'uploading') {
      const newFileList = fileList.slice(-1);
      setFileList(newFileList);
      
      if (newFileList.length === 0) {
        form.setFieldsValue({ pv_session: undefined });
      }
    }
  };

  return (
    <Form form={form} layout="vertical">
      <Modal
      maskClosable={false}
      destroyOnClose={true}
        title="Confirmation des notes"
        open={modalVisible}
        width={1200}
        okText="Soumettre"
        cancelText="Annuler"
        onCancel={() => setModalVisible(false)}
        onOk={soumettreNote}
      >
        <p>
          Êtes-vous sûr de vouloir soumettre ces notes ? Cette action est irréversible.
        </p>
  
      
        <Form.Item
          name="pv_session"
          label="Upload pv"
          htmlFor={null}
          rules={[{ required: true, message: "Veuillez remplir ce champ." }]}
        >
          <Dragger
            disabled={params.state?.type === "consulter" || modalVisible}
            onChange={handleChangeUpload}
            fileList={fileList}
            beforeUpload={() => false}
            maxCount={1}
            listType="picture"
            showUploadList={{
                    showRemoveIcon: !(params.state?.type === "consulter" || modalVisible),
                  }}
            accept=".jpg,.jpeg,.png,.pdf"
            onPreview={(file) => {
              if (file?.originFileObj) {
                const url = URL.createObjectURL(file?.originFileObj);
                window.open(url, "_blank");
              } else if (file?.url) {
                window.open(file?.url, "_blank");
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Cliquez ou déposez votre fichier ici pour le télécharger - انقر
              أو اسحب ملفك هنا لتحميله.
            </p>
            <p className="ant-upload-hint">
              Formats acceptés - الملفات المسموح بها : .jpg, .jpeg, .png,
              .pdf.
            </p>
          </Dragger>
        </Form.Item>
        
        <Table
          className="compact-table"
          columns={columns}
          dataSource={dataSource}
          bordered
          size="small"
          pagination={true}
          disabled={true}
          scroll={{ x: "max-content" }}
        />
      </Modal>

    
  <Button
          className={classes.buttonCreate}
          onClick={() => navigate(-1)}
          style={{marginInline:"16px"}}

        >
          <CircleArrowLeft strokeWidth={1.25} />
          {t("Précédent")}
        </Button>
      <Card
        className={classes.listeCard}
        title={t(`Saisie des notes du ${periode}`)}
      >
        <Form.Item
          name="pv_session"
          label="Upload pv"
          rules={[{ required: true, message: "Veuillez remplir ce champ." }]}
        >
          <Dragger
            disabled={params.state?.type === "consulter"}
            onChange={handleChangeUpload}
            fileList={fileList}
            beforeUpload={() => false}
            maxCount={1}
            listType="picture"
            accept=".jpg,.jpeg,.png,.pdf"
            onPreview={(file) => {
              if (file?.originFileObj) {
                const url = URL.createObjectURL(file?.originFileObj);
                window.open(url, "_blank");
              } else if (file?.url) {
                window.open(file?.url, "_blank");
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Cliquez ou déposez votre fichier ici pour le télécharger - انقر
              أو اسحب ملفك هنا لتحميله.
            </p>
            <p className="ant-upload-hint">
              Formats acceptés - الملفات المسموح بها : .jpg, .jpeg, .png,
              .pdf.
            </p>
          </Dragger>
        </Form.Item>

        <Table
          className="compact-table"
          columns={columns}
          dataSource={dataSource}
          bordered
          size="small"
          pagination={true}
          scroll={{ x: "max-content" }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <Button key="submit" type="primary" onClick={handleSubmit}>
            Valider
          </Button>
        </div>
      </Card>
    </Form>
  );
};

export default SaisieNotes;